import { useState } from 'react';
import { GitHubScaffoldService } from '../../services/githubScaffoldService';
import { logger } from '../../services/loggingService';
import toast from 'react-hot-toast';

interface GitHubScaffoldPanelProps {
  manifestId: string;
  userId: string;
  projectId: string;
}

export function GitHubScaffoldPanel({ manifestId, userId, projectId }: GitHubScaffoldPanelProps) {
  const [scaffolding, setScaffolding] = useState(false);
  const [config, setConfig] = useState({
    owner: '',
    repo: '',
    branch: 'main',
    token: '',
  });

  const handleScaffold = async () => {
    if (!config.owner || !config.repo || !config.token) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setScaffolding(true);
      await GitHubScaffoldService.scaffoldRepository({
        userId,
        projectId,
        repoName: config.repo,
        repoDescription: `Scaffolded from manifest ${manifestId}`,
        isPrivate: false,
        manifestData: {
          files: []
        }
      });
      toast.success('Repository scaffolded successfully!');
    } catch (error) {
      logger.error('Failed to scaffold repository', { error, manifestId, projectId, userId });
      toast.error((error as Error).message || 'Failed to scaffold repository');
    } finally {
      setScaffolding(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-surface rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-bold text-text mb-6">
          Scaffold GitHub Repository
        </h2>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Repository Owner *
            </label>
            <input
              type="text"
              value={config.owner}
              onChange={(e) => setConfig(prev => ({ ...prev, owner: e.target.value }))}
              className="input-base w-full focus:ring-primary"
              placeholder="e.g., your-username or org-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Repository Name *
            </label>
            <input
              type="text"
              value={config.repo}
              onChange={(e) => setConfig(prev => ({ ...prev, repo: e.target.value }))}
              className="input-base w-full focus:ring-primary"
              placeholder="e.g., my-project"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Default Branch
            </label>
            <input
              type="text"
              value={config.branch}
              onChange={(e) => setConfig(prev => ({ ...prev, branch: e.target.value }))}
              className="input-base w-full focus:ring-primary"
              placeholder="main"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              GitHub Personal Access Token *
            </label>
            <input
              type="password"
              value={config.token}
              onChange={(e) => setConfig(prev => ({ ...prev, token: e.target.value }))}
              className="input-base w-full focus:ring-primary"
              placeholder="ghp_..."
            />
            <p className="mt-2 text-sm text-text-secondary">
              Required scopes: repo, workflow
            </p>
          </div>

          <div className="alert alert-default">
            <h3 className="text-sm font-semibold mb-2">
              What will be created:
            </h3>
            <ul className="space-y-1 text-sm">
              <li>✓ Complete file structure from manifest</li>
              <li>✓ Configuration files (package.json, tsconfig.json, etc.)</li>
              <li>✓ CI/CD workflows (.github/workflows)</li>
              <li>✓ Branch protection rules</li>
              <li>✓ Required status checks</li>
              <li>✓ Initial commit to {config.branch}</li>
            </ul>
          </div>

          <button
            onClick={handleScaffold}
            disabled={scaffolding || !config.owner || !config.repo || !config.token}
            className="btn-success w-full disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {scaffolding ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Scaffolding Repository...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Scaffold Repository
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
